import React, { useState, useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import findIndex from 'lodash.findindex';

import ItemAddedDialog from '../dialogs/ItemAddedDialog';
import LoadingDialog from '../dialogs/LoadingDialog';
import LoadMoreButton from './LoadMoreButton';
import MediaListItem from './MediaListItem';
import MediaListMasonry from './MediaListMasonry';
import ThumbnailPreview from '../thumbnail/ThumbnailPreview';
import withRequestHandler from '../../hoc/withRequestHandler';
import { FormattedMessageWrappedInSpan } from '../../misc';

import AddFotoFlatToCartMutation from '../../../mutations/AddFotoFlatToCartMutation';
import AddSingleFotoToCartMutation from '../../../mutations/AddSingleFotoToCartMutation';
import RequestRecheckMutation from '../../../mutations/RequestRecheckMutation';

import { getFromLocalStorage, setToLocalStorage } from '../../../utils/browser-storage';
import { trackEvent } from '../../../utils/ga-tracking';

import {
  FOTO_FLAT,
  SINGLE_PHOTO,
  PARTICIPANT,
  IMPRESSION,
  ACCOUNT,
} from '../../../utils/variables';
import { getProduct } from '../../../utils/common';
import { HelpdeskWidgetContext } from '../HelpdeskWidget';

const MediaList = (props) => {

  const [slideIndex, setSlideIndex] = useState(0);
  const [email, setEmail] = useState(getFromLocalStorage('email') || '');
  const [updatedCart, setUpdatedCart] = useState(null);
  const [showThumbnailPreview, setShowThumbnailPreview] = useState(false);
  const [showRecheckView, setShowRecheckView] = useState(false);
  const [rechecks, setRechecks] = useState([]);
  const [showItemAddedDialog, setShowItemAddedDialog] = useState(false);
  const [fotoFlat, setFotoFlat] = useState(null);
  const [singlePhoto, setSinglePhoto] = useState(null);
  const [hlpDskState, hlpDskDispatch] = useContext(HelpdeskWidgetContext);

  // Direct call of `useEffect` is required for test function
  // mock to propely spy on this function and call the `useEffect`
  // which is currently not supporter by the test framework on its
  // own.
  React.useEffect(() => {
    if (props.listType !== 'account') {
      setFotoFlat(getProduct(props.offer.products, FOTO_FLAT));
      setSinglePhoto(getProduct(props.offer.products, SINGLE_PHOTO));
    }
  }, [])

  const handleCloseItemAddedDialog = () => {
    setShowItemAddedDialog(false);
  };

  const handleOpenThumbnailPreview = mediaId => {
    const index = findIndex(props.media.edges, edge => edge.node.id === mediaId);
    if (index >= props.media.edges.length - 5) {
      if (props.hasMore) {
        props.onLoadMore();
      }
    }
    setShowThumbnailPreview(true);
    setSlideIndex(index);
  };

  const handleCloseThumnailPreview = () => {
    setShowThumbnailPreview(false);
    setShowRecheckView(false);
  };

  const handleShowRecheckView = () => {
    setShowRecheckView(true);
  };

  const handleHideRecheckView = () => {
    setShowRecheckView(false);
  };

  const handleSlide = index => {
    if (index >= props.media.edges.length - 5) {
      if (props.hasMore) {
        props.onLoadMore();
      }
    }
    setSlideIndex(index);
  };

  const handleBuyFotoFlat = () => {
    props.onRequestStart();
    const { startnumber, relay } = props;

    hlpDskDispatch({
      type: 'addToHistory',
      payload: `buy FF: ${startnumber}`
    })

    AddFotoFlatToCartMutation.commit(
      relay.environment,
      fotoFlat.id,
      startnumber,
      (error, cart) => {
        if (error) {
          props.onRequestError(error);
        } else {
          setUpdatedCart(cart);
          setShowItemAddedDialog(true);
        }
        props.onRequestStop();
      }
    );
  };

  const handleBuySinglePhoto = () => {
    props.onRequestStart();
    const { relay, media } = props;

    hlpDskDispatch({
      type: 'addToHistory',
      payload: `buy single photo: ${media.edges[slideIndex].node.sgId}`
    })

    AddSingleFotoToCartMutation.commit(
      relay.environment,
      singlePhoto.id, // product id
      media.edges[slideIndex].node.id, // foto media id
      (error, cart) => {
        if (error) {
          props.onRequestError(error);
        } else {
          setUpdatedCart(cart);
          setShowItemAddedDialog(true);
        }
        props.onRequestStop();
      }
    );
  };

  const handleRequestRecheck = (mediaId, startnumber, email) => {

    hlpDskDispatch({
      type: 'addToHistory',
      payload: `recheck: ${startnumber} on ${mediaId}`
    })

    props.onRequestStart();
    setShowRecheckView(false);
    const { relay, media, intl } = props;
    RequestRecheckMutation.commit(
      relay.environment,
      email,
      intl.locale,
      media.edges[slideIndex].node.id,
      startnumber,
      (error, status) => {
        if (!error) {
          setToLocalStorage('email', email);
          setEmail(email);
          setRechecks([...rechecks, mediaId])
        } else {
          props.onRequestError(error);
        }
        props.onRequestStop();
      }
    );
  };

  const _getFotoFlatOffer = () => {
    const { offer, listType } = props;

    // 1.
    // The Foto-Flat offer should only be visible in the participant list and the impressions
    // list
    if (listType !== PARTICIPANT && listType !== IMPRESSION) {
      return null;
    }

    const fotoFlatProduct = getProduct(offer.products, FOTO_FLAT);
    const singlePhotoProduct = getProduct(offer.products, SINGLE_PHOTO);

    // 2.
    // The Foto-Flat should only be shown if the the customer would make a good deal
    if (fotoFlatProduct) {
      if (_shouldFotoFlatBeDisplayed(fotoFlatProduct, singlePhotoProduct)) {
        return {
          id: fotoFlatProduct.id,
          price: fotoFlatProduct.price,
          voucher: offer.voucherCampaign,
        };
      }
    }

    return null;
  }

  const _shouldFotoFlatBeDisplayed = (fotoFlat, singlePhoto) => {
    // When an event does not offer a single photo product the FotoFlat should definetly
    // be displayed
    if (!singlePhoto) {
      return true;
    }

    // The Foto-Flat offer should only be shown if the price of found photos for a
    // participant in their sum is higher than the price of a Foto-Flat
    let minCountNecessary = 10000;
    if (singlePhoto.price.amount !== 0) { // avoid division by zero
      minCountNecessary = fotoFlat.price.amount / singlePhoto.price.amount;
    }
    return props.participantMediaCount >= minCountNecessary;
  }


  const {
    eventHasSearchByFace,
    eventHasSecondFactor,
    media,
    hasMore,
    startNumbers,
    hasMorePhotosButton,
    isFetching,
    isLoading,
    canRestartUpload,
    participantMediaCount,
    impressionMediaCount,
    startnumber,
    eventSgId,
    listType,
    onLoadMore,
    onScrollToImpressions,
    onScrollToVoucher,
    onScrollToExtendedSearch,
    onImagesLoaded,
    noSearchingByStartNumber,
    hasCertificates
  } = props;

  const fotoFlatOffer = _getFotoFlatOffer();

  return (
    <div className="row">
      <div className="col-xs-16 col-sm-12 col-sm-offset-2">
        <MediaListMasonry
          participantMediaCount={participantMediaCount}
          impressionMediaCount={impressionMediaCount}
          fotoFlatOffer={fotoFlatOffer}
          onBuyFotoFlatClick={handleBuyFotoFlat}
          onScrollToImpressions={onScrollToImpressions}
          onScrollToVoucher={onScrollToVoucher}
          onImagesLoaded={onImagesLoaded}
          listType={listType}
        >
          {media.edges.map(({ node: mediaNode }) => (
            <MediaListItem
              key={mediaNode.id}
              item={mediaNode}
              onShowPreview={handleOpenThumbnailPreview}
              isAccountEventPage={listType === ACCOUNT}
            />
          ))}
        </MediaListMasonry>
        {hasMore && <LoadMoreButton showSpinner={isFetching} onClick={onLoadMore} />}
        {hasMorePhotosButton && !hasMore && listType === 'participant' && (
          <div className="medialist-scroll-to-extended-search">
            <button
              className="primary"
              onClick={() => {
                trackEvent('Event', 'Search', 'Are there more photos of me button');
                onScrollToExtendedSearch();
              }}
            >
              <FormattedMessageWrappedInSpan
                id="mediaList.extendedSearchHintButton"
                defaultMessage="+ Are there more photos of me?"
              />
            </button>
          </div>
        )}
        <ThumbnailPreview
          eventHasSearchByFace={eventHasSearchByFace}
          eventHasSecondFactor={eventHasSecondFactor}
          open={!showItemAddedDialog && showThumbnailPreview}
          media={media}
          eventSgId={eventSgId}
          rechecks={rechecks}
          slideIndex={slideIndex}
          startnumber={startnumber}
          fotoFlatProduct={fotoFlatOffer}
          singlePhotoProduct={singlePhoto}
          showRecheckView={showRecheckView}
          email={email}
          startNumbers={startNumbers}
          isLoading={isLoading}
          isAccountEventPage={listType === ACCOUNT}
          canRestartUpload={canRestartUpload}
          onSlide={handleSlide}
          onBuySinglePhoto={handleBuySinglePhoto}
          onBuyFotoFlat={handleBuyFotoFlat}
          onShowRecheckView={handleShowRecheckView}
          onHideRecheckView={handleHideRecheckView}
          onRequestRecheck={handleRequestRecheck}
          onRequestClose={handleCloseThumnailPreview}
          noSearchingByStartNumber={noSearchingByStartNumber}
          hasCertificates={hasCertificates}
        />
        {updatedCart && (
          <ItemAddedDialog
            open={showItemAddedDialog}
            lineItems={updatedCart.lineItems}
            totalPrice={updatedCart.totalPrice}
            onRequestClose={handleCloseItemAddedDialog}
          />
        )}
        <LoadingDialog open={isLoading} />
      </div>
    </div>
  );
}

MediaList.defaultProps = {
  eventHasSearchByFace: false,
  eventHasSecondFactor: false,
};

MediaList.propTypes = {
  eventHasSearchByFace: PropTypes.bool,
  eventHasSecondFactor: PropTypes.bool,
  media: PropTypes.object,
  offer: PropTypes.object,
  hasMore: PropTypes.bool,
  hasMorePhotosButton: PropTypes.bool,
  isFetching: PropTypes.bool,
  isLoading: PropTypes.bool,
  canRestartUpload: PropTypes.bool,
  participantMediaCount: PropTypes.number,
  impressionMediaCount: PropTypes.number,
  eventSgId: PropTypes.number,
  startnumber: PropTypes.string,
  startNumbers: PropTypes.array,
  onLoadMore: PropTypes.func,
  onScrollToImpressions: PropTypes.func,
  onScrollToVoucher: PropTypes.func,
  onScrollToExtendedSearch: PropTypes.func,
  onRequestStart: PropTypes.func,
  onRequestStop: PropTypes.func,
  onRequestError: PropTypes.func,
  listType: PropTypes.oneOf([
    'participant',
    'categories',
    'suggestions',
    'dual',
    'gps',
    'extended',
    'impressions',
    'account',
  ]).isRequired,
  relay: PropTypes.object,
  intl: PropTypes.object,
  onImagesLoaded: PropTypes.func,
  noSearchingByStartNumber: PropTypes.bool,
  hasCertificates: PropTypes.bool
};

export { MediaList };

const WrappedComponent = withRequestHandler(MediaList);

export default createFragmentContainer(injectIntl(WrappedComponent), {
  media: graphql`
    fragment MediaList_media on MediaConnection {
      edges {
        node {
          id
          sgId
          shotDate
          variants {
            name
            url
          }
          ...MediaListItem_item
        }
      }
    }
  `,
  offer: graphql`
    fragment MediaList_offer on Event {
      products {
        id
        mediaCollection {
          name
        }
        price
        type
      }
      voucherCampaign {
        banner
        name
        shortText
      }
    }
  `,
});
